import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import {
  countMissingDefault,
  countNonDefault,
  describeSkipped,
  formatCount,
  mergeRetryResult,
  runBulkPasswordChunks,
  validateBulkPassword,
} from '../lib/bulk-password';
import {
  ConfirmStep,
  PasswordFormStep,
  ResultStep,
  RunningStep,
} from './bulk-password-steps';

const STEP = {
  form: 'form',
  confirm: 'confirm',
  running: 'running',
  result: 'result',
};

function rowsById(rows, getId) {
  return new Map(rows.map((row) => [String(getId(row)), row]));
}

const hasDefault = (row, getDefault) =>
  String(getDefault(row) ?? '').trim() !== '';

/**
 * Modal ganti password massal, dipakai Data Siswa dan Data Guru.
 * Langkah: form -> confirm -> running -> result (spec §5.2).
 *
 * `submitChunk({ ids, mode, password })` mengirim SATU potongan (maks. 200 ID)
 * dan mengembalikan `{ updated, skipped, notFound }`. "Selesai" (atau menutup
 * modal di layar hasil) memanggil `onDone()` lalu `onClose()`: data sudah
 * berubah, jadi pemanggil perlu mengosongkan seleksi dan memuat ulang list.
 */
function BulkPasswordModal({
  isOpen,
  onClose,
  onDone,
  rows,
  entityLabel,
  defaultLabel,
  getId,
  getName,
  getDefault,
  submitChunk,
}) {
  const [step, setStep] = useState(STEP.form);
  const [mode, setMode] = useState('default');
  const [password, setPassword] = useState('');
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep(STEP.form);
    setMode('default');
    setPassword('');
    setHasTriedSubmit(false);
    setResult(null);
  }, [isOpen]);

  const summary = useMemo(() => {
    const targets =
      mode === 'default'
        ? rows.filter((row) => hasDefault(row, getDefault))
        : rows;
    return {
      changeCount: targets.length,
      nonDefaultCount: countNonDefault(targets, getDefault),
      missingCount: countMissingDefault(rows, getDefault),
    };
  }, [rows, mode, getDefault]);

  const passwordError =
    mode === 'custom' && hasTriedSubmit ? validateBulkPassword(password) : null;

  const goToConfirm = () => {
    setHasTriedSubmit(true);
    if (mode === 'custom' && validateBulkPassword(password)) {
      inputRef.current?.focus();
      return;
    }
    setStep(STEP.confirm);
  };

  const run = async (ids, previous) => {
    setProgress({ processed: 0, total: ids.length });
    setStep(STEP.running);
    const outcome = await runBulkPasswordChunks({
      ids,
      mode,
      password: mode === 'custom' ? password : null,
      submitChunk,
      onProgress: (processed, total) => setProgress({ processed, total }),
    });
    setResult(previous ? mergeRetryResult(previous, outcome) : outcome);
    setStep(STEP.result);
  };

  const finish = () => {
    onDone();
    onClose();
  };

  const isRunning = step === STEP.running;
  const handleClose = () => {
    if (isRunning) return;
    if (step === STEP.result) finish();
    else onClose();
  };

  const allIds = rows.map((row) => String(getId(row)));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      isCentered
      closeOnOverlayClick={!isRunning}
      closeOnEsc={!isRunning}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader as="h2" fontSize="lg" pr={12}>
          Ganti password {formatCount(rows.length)} {entityLabel}
        </ModalHeader>
        {!isRunning && <ModalCloseButton aria-label="Tutup" />}
        <ModalBody>
          {step === STEP.form && (
            <PasswordFormStep
              entityLabel={entityLabel}
              defaultLabel={defaultLabel}
              mode={mode}
              onModeChange={setMode}
              password={password}
              onPasswordChange={setPassword}
              error={passwordError}
              inputRef={inputRef}
              onSubmit={goToConfirm}
            />
          )}
          {step === STEP.confirm && (
            <ConfirmStep mode={mode} defaultLabel={defaultLabel} {...summary} />
          )}
          {isRunning && <RunningStep {...progress} />}
          {step === STEP.result && result && (
            <ResultStep
              result={result}
              skippedItems={describeSkipped(
                result,
                rowsById(rows, getId),
                getName,
              )}
              onRetry={() => run(result.failedIds, result)}
            />
          )}
        </ModalBody>
        <ModalFooter gap={3}>
          {step === STEP.form && (
            <>
              <Button variant="ghost" onClick={onClose}>
                Batal
              </Button>
              <Button colorScheme="telegram" onClick={goToConfirm}>
                Lanjut
              </Button>
            </>
          )}
          {step === STEP.confirm && (
            <>
              <Button variant="ghost" onClick={() => setStep(STEP.form)}>
                Kembali
              </Button>
              <Button
                colorScheme="red"
                isDisabled={summary.changeCount === 0}
                onClick={() => run(allIds)}
              >
                Ganti password {formatCount(summary.changeCount)} akun
              </Button>
            </>
          )}
          {step === STEP.result && (
            <Button colorScheme="telegram" onClick={finish}>
              Selesai
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default BulkPasswordModal;
